// src/pages/Lounge/Clubs/PartnerDashboard/PartnerDashboard.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";
import cls from "./PartnerDashboard.module.scss";

import avatarPng from "@/assets/UI/avatar.png";

const ALLOWED_STATES = new Set(["activeClubPartner"]);

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const getVideoIdFromPost = (post) => {
  const v = post?.videoId;
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?._id) return v._id;
  return "";
};

const PartnerDashboard = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // keep top tabs row exactly like ClubHome pattern
  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const [feedLoading, setFeedLoading] = useState(true);
  const [feedErr, setFeedErr] = useState("");
  const [posts, setPosts] = useState([]);

  const [moderatingId, setModeratingId] = useState("");
  const [moderationErr, setModerationErr] = useState("");

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}`);
        if (ignore) return;

        const memberState = safeStr(data?.memberState);
        if (!ALLOWED_STATES.has(memberState)) {
          navigate(`/lounge/clubs/single/${clubId}`, { replace: true });
          return;
        }

        setPayload(data || null);
      } catch (err) {
        if (ignore) return;
        setError(
          err?.response?.data?.message ||
            t("partnerDashboard.states.error", "Something went wrong."),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, navigate, t]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setFeedLoading(true);
        setFeedErr("");

        const res = await api.get(`/v1/posts/feeds/clubs/${clubId}/pending`, {
          params: { page: 1, limit: 15 },
        });

        const rows =
          res?.data?.rows ||
          res?.data?.posts ||
          res?.data?.data ||
          res?.data?.items ||
          [];

        if (ignore) return;
        setPosts(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (ignore) return;
        setFeedErr(
          err?.response?.data?.message ||
            t("partnerDashboard.feed.error", "Failed to load pending posts."),
        );
        setPosts([]);
      } finally {
        if (!ignore) setFeedLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, t]);

  const club = payload?.club || null;
  const clubName =
    safeStr(club?.clubName) || t("partnerDashboard.header.clubName", "Club");

  const clubNo = club?.clubNo != null ? String(club.clubNo) : "";
  const streetName = safeStr(club?.streetName);
  const cityName = safeStr(club?.cityName);

  const clubAddressParts = [
    hasText(clubNo)
      ? `${t("partnerDashboard.header.clubNo", "Club")} ${clubNo}`
      : "",
    streetName,
    cityName,
  ].filter((x) => hasText(x));

  const clubAddress = clubAddressParts.length
    ? clubAddressParts.join(" · ")
    : t("partnerDashboard.header.addressFallback", "Address not available");

  const clubAvatarSrc =
    club?.clubAvatarThumb?.url || club?.clubAvatar?.url || avatarPng;

  const clubBase = `/lounge/clubs/single/${clubId}/partner-dashboard`;
  const clubTabs = [
    {
      key: "pending-posts",
      label: t("partnerDashboard.tabs.pendingPosts", "Pending Post"),
      to: clubBase,
    },
    {
      key: "pending-requests",
      label: t("partnerDashboard.tabs.pendingRequests", "Pending request"),
      to: `${clubBase}/pending-requests`,
    },
  ];

  const isActiveTab = (to) => {
    const p = safeStr(location.pathname);
    if (to.endsWith("/pending-requests")) return p.startsWith(to);
    if (p === clubBase || p.startsWith(`${clubBase}/`)) return true;
    return p === to;
  };

  const renderFeedPost = (post) => {
    const type = safeStr(post?.type).toLowerCase();
    const hasImages = Array.isArray(post?.images) && post.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(post);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={post} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={post} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={post} />;

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  const handleModerate = async (postId, status) => {
    if (!postId || moderatingId) return;

    try {
      setModeratingId(postId);
      setModerationErr("");
      await api.patch(`/v1/posts/${postId}/moderate`, { status });
      setPosts((prev) => prev.filter((p) => p?._id !== postId));
    } catch (err) {
      setModerationErr(
        err?.response?.data?.message ||
          t("partnerDashboard.feed.moderationError", "Action failed."),
      );
    } finally {
      setModeratingId("");
    }
  };

  // ✅ Loader gate AFTER hooks
  if (loading && !payload && !error) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      {/* ✅ Top nav (same as ClubHome) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.topTabs.aria", "Lounge")}
      >
        <Link
          to="/lounge"
          className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.public", "Public")}
        </Link>
        <Link
          to="/lounge/friends-feed"
          className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.friends", "Friends")}
        </Link>
        <Link
          to="/lounge/clubs-feed"
          className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.clubs", "Clubs")}
        </Link>
        <Link
          to="/lounge/chat"
          className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.chat", "Chat")}
        </Link>
        <Link
          to="/lounge/search"
          className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
        >
          🔍 {t("clubHome.topTabs.search", "Search")}
        </Link>
      </nav>

      {/* ✅ Header (same visual style as ClubHome) */}
      <header className={cls.header}>
        <div className={cls.headerMain}>
          <div className={cls.avatarWrap}>
            <img className={cls.avatar} src={clubAvatarSrc} alt={clubName} />
            <span className={cls.badgeOnline}>
              {t("clubHome.header.active", "Active")}
            </span>
          </div>

          <div className={cls.headerInfo}>
            <div className={cls.displayName}>{clubName}</div>

            <div className={cls.usernameRow}>
              <span className={cls.memberSince}>{clubAddress}</span>
            </div>

            <div className={cls.metaRow}>
              <Link
                className={cls.btnPrimary}
                to={`/lounge/clubs/single/${clubId}/home`}
              >
                {t("partnerDashboard.header.backToClub", "Back to Club")}
              </Link>
            </div>

            {loading ? (
              <div className={cls.stateRow}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>
                  {t("partnerDashboard.states.loading", "Loading club...")}
                </span>
              </div>
            ) : null}

            {!loading && error ? (
              <div className={cls.stateRowError}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>{error}</span>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* ✅ Second nav (same tabs component as ClubHome) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.clubTabs.aria", "Club")}
      >
        {clubTabs.map((tab) => {
          const active = isActiveTab(tab.to);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`${cls.tabButton} ${active ? cls.activeTab : ""}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* ✅ Content */}
      <div className={cls.centerCol}>
        {moderationErr ? (
          <div className={cls.feedStateBad}>{moderationErr}</div>
        ) : null}

        {feedLoading ? (
          <div className={cls.feedState}>
            {t("partnerDashboard.feed.loading", "Loading pending posts...")}
          </div>
        ) : feedErr ? (
          <div className={cls.feedStateBad}>{feedErr}</div>
        ) : posts.length === 0 ? (
          <div className={cls.feedState}>
            {t("partnerDashboard.feed.empty", "No pending posts.")}
          </div>
        ) : (
          <div className={cls.postList}>
            {posts.map((post) => (
              <div key={post._id}>
                {renderFeedPost(post)}

                <div className={cls.moderationRow}>
                  <button
                    type="button"
                    className={`${cls.moderationBtn} ${cls.approveBtn}`}
                    onClick={() => handleModerate(post._id, "published")}
                    disabled={moderatingId === post._id}
                  >
                    {t("partnerDashboard.feed.accept", "Accept")}
                  </button>

                  <button
                    type="button"
                    className={`${cls.moderationBtn} ${cls.rejectBtn}`}
                    onClick={() => handleModerate(post._id, "rejected")}
                    disabled={moderatingId === post._id}
                  >
                    {t("partnerDashboard.feed.reject", "Reject")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
